import React, { FC, useMemo, useState } from 'react'
import { Checkbox, Typography } from '@mui/material'
import { cloneDeep } from 'lodash'
import moment from 'moment'
import { Statsig } from 'statsig-react'

import NoFloorPlan from '../../assets/imgs/noFloorPlan.png'
import CheckImage from './checkImage'
import { AvailableUnit, FloorPlanModeUnitType, OtherUnitInfoProps, SelectedUnitInfoProps, SelectedUnitsType } from './contantsAndType'

interface ScheduleTourFloorPlanProps {
  questionnaireId: string
  propertyId: string
  floorPlans: FloorPlanModeUnitType[]
  selectedUnits: SelectedUnitsType
  onchange: (selectedUnits: SelectedUnitsType) => void
}
const ScheduleTourFloorPlan: FC<ScheduleTourFloorPlanProps> = props => {
  const { floorPlans, selectedUnits, propertyId, onchange, questionnaireId } = props
  const isShowPic = useMemo(() => floorPlans?.some(item => item?.floorUrl), [floorPlans])
  const onSelectValueChange = (value: { unitsInfo: SelectedUnitInfoProps; otherUnit: OtherUnitInfoProps } | undefined, key: string) => {
    const newSelectedUnits = new Map(selectedUnits)
    if (value) {
      newSelectedUnits.set(key, value)
    } else {
      newSelectedUnits.delete(key)
    }
    onchange(newSelectedUnits)
  }
  return (
    <div className="px-5 py-2 lg:py-4  lg:pt-0 lg:px-10 grid grid-cols-1 gap-y-5">
      {floorPlans.map(floor => (
        <ScheduleTourFloorPlanItem
          propertyId={propertyId}
          isShowPic={isShowPic}
          questionnaireId={questionnaireId}
          key={`${floor.floorPlanName}-${floor.floorPlanIndex}`}
          floorPlan={floor}
          value={selectedUnits.get(`${floor.floorPlanName}-${floor.floorPlanIndex}`)}
          onChange={value => {
            onSelectValueChange(value, `${floor.floorPlanName}-${floor.floorPlanIndex}`)
          }}
        />
      ))}
    </div>
  )
}

interface ScheduleTourFloorPlanItemProps {
  questionnaireId: string
  floorPlan: FloorPlanModeUnitType
  propertyId: string
  isShowPic: boolean
  value: { unitsInfo: SelectedUnitInfoProps; otherUnit: OtherUnitInfoProps } | undefined
  onChange: (value: { unitsInfo: SelectedUnitInfoProps; otherUnit: OtherUnitInfoProps } | undefined) => void
}
const ScheduleTourFloorPlanItem: FC<ScheduleTourFloorPlanItemProps> = props => {
  const { floorPlan, value, onChange, propertyId, questionnaireId, isShowPic } = props
  const [checkImage, setCheckImage] = useState(false)
  const floorPlanPrice = useMemo(() => {
    const { availableUnits } = floorPlan
    let minPrice = 0
    let maxPrice = 0
    const isNull = availableUnits.every(unit => !Array.isArray(unit.rentPrices) || unit.rentPrices.length === 0)
    if (isNull) return { minPrice, maxPrice }

    availableUnits.forEach(unit => {
      if (!Array.isArray(unit.rentPrices) || unit.rentPrices.length === 0) return
      const price = Number(unit.rentPrices[0].discountAmount)
      if (minPrice === 0) {
        minPrice = price
      }
      if (price < minPrice) minPrice = price
      if (price >= maxPrice) maxPrice = price
    })
    return {
      minPrice: Number(minPrice / 100),
      maxPrice: Number(maxPrice / 100),
    }
  }, [floorPlan])
  const floorPlanTitle = useMemo(() => {
    const { floorPlanBedroomNum, floorPlanBathroomNum, floorPlanName } = floorPlan

    const planName = floorPlanName ? ` - ${floorPlanName}` : ''
    if (Number(floorPlanBedroomNum) === 0) {
      return `Studio${planName}`
    }
    return `${Number(floorPlanBedroomNum)} Bed ${Number(floorPlanBathroomNum)} Bath${planName}`
  }, [floorPlan])
  const floorPlanArea = useMemo(() => {
    const minArea = Number(floorPlan.minArea)
    const maxArea = Number(floorPlan.maxArea)
    return minArea === maxArea ? `${minArea} sqft` : `${minArea} - ${maxArea} sqft`
  }, [floorPlan])
  const selectFloorPlan = useMemo(() => value && value.unitsInfo.floorPlanName === floorPlan.floorPlanName, [value, floorPlan])

  const onCheckImage = () => {
    Statsig.logEvent('schedule_tour_click_unit_image', window.location.href, {
      questionnaireId,
      floorPlanName: floorPlan.floorPlanName,
      floorUrl: floorPlan.floorUrl,
      propertyId,
    })
    setCheckImage(true)
  }
  const changeFloorPlanSelect = () => {
    if (value) {
      onChange(undefined)
    } else {
      onChange({
        unitsInfo: { floorPlanIndex: floorPlan.floorPlanIndex, floorPlanName: floorPlan.floorPlanName, units: [], floorPlanTitle },
        otherUnit: { floorPlanIndex: -1, floorPlanName: '', name: '', floorPlanTitle },
      })
    }
  }
  const changeUnitItemSelect = (unit: AvailableUnit) => {
    const { id, name, availableStatus, preTourEndDate, preTourStartDate, availableStartTime } = unit
    if (value) {
      const newValue = cloneDeep(value)
      const { units } = newValue.unitsInfo
      const target = units.findIndex(u => String(u.id) === String(id))
      if (target > -1) {
        units.splice(target, 1)
      } else {
        units.push({ name, id: Number(id), availableStatus, availableStartTime, preTourStartDate, preTourEndDate })
      }
      onChange(newValue)
    } else {
      onChange({
        unitsInfo: {
          floorPlanTitle,
          floorPlanIndex: floorPlan.floorPlanIndex,
          floorPlanName: floorPlan.floorPlanName,
          units: [{ name, id: Number(id), availableStatus, availableStartTime, preTourStartDate, preTourEndDate }],
        },
        otherUnit: { floorPlanTitle, floorPlanIndex: -1, floorPlanName: '', name: '' },
      })
    }
  }

  const changeOtherUnitSelect = () => {
    if (value) {
      const newValue = cloneDeep(value)
      if (newValue.otherUnit.floorPlanIndex === floorPlan.floorPlanIndex) {
        newValue.otherUnit.floorPlanIndex = -1
        newValue.otherUnit.floorPlanName = ''
      } else {
        newValue.otherUnit.floorPlanIndex = floorPlan.floorPlanIndex
        newValue.otherUnit.floorPlanName = floorPlan.floorPlanName
      }
      onChange(newValue)
    } else {
      onChange({
        unitsInfo: { floorPlanTitle, floorPlanIndex: floorPlan.floorPlanIndex, floorPlanName: floorPlan.floorPlanName, units: [] },
        otherUnit: { floorPlanTitle, floorPlanIndex: floorPlan.floorPlanIndex, floorPlanName: floorPlan.floorPlanName, name: '' },
      })
    }
  }
  const changeOtherUnitInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.trim()
    if (value) {
      const newValue = cloneDeep(value)
      newValue.otherUnit.name = input
      if (input) {
        newValue.otherUnit.floorPlanIndex = floorPlan.floorPlanIndex
        newValue.otherUnit.floorPlanName = floorPlan.floorPlanName
        newValue.otherUnit.floorPlanTitle = floorPlanTitle
      }
      onChange(newValue)
    } else if (input) {
      onChange({
        unitsInfo: { floorPlanTitle, floorPlanIndex: floorPlan.floorPlanIndex, floorPlanName: floorPlan.floorPlanName, units: [] },
        otherUnit: { floorPlanTitle, floorPlanIndex: floorPlan.floorPlanIndex, floorPlanName: floorPlan.floorPlanName, name: input },
      })
    }
  }
  return (
    <div className={`p-4 border rounded-lg ${selectFloorPlan ? 'border-[#050A22]' : 'border-[#E0E2EF]'}`}>
      <CheckImage
        src={floorPlan.floorUrl}
        alt=""
        onClose={() => {
          setCheckImage(false)
        }}
        visible={checkImage}
      />
      <div className="flex">
        {isShowPic ? (
          <img alt="" src={floorPlan.floorUrl || NoFloorPlan} aria-hidden="true" onClick={onCheckImage} className="w-[88px] h-[88px]" />
        ) : null}
        <div aria-hidden="true" onClick={changeFloorPlanSelect} className="ml-4 flex-grow flex justify-between flex-col">
          <div className="w-full relative mb-[5px]">
            <div className="mr-[26px]">
              <Typography className="!font-Averta6 !font-semibold !text-base !text-[#050A22] break-all">{floorPlanTitle}</Typography>
              <Typography className="table px-2 py-0.5 rounded-[18px] !text-[10px] !text-[#050A22] !font-Averta4 bg-slate-50">
                {floorPlanArea}
              </Typography>
            </div>
            <Checkbox
              className="!absolute !top-0 !right-0"
              sx={{
                color: '#E0E2EF',
                padding: '0',
                '&.Mui-checked': {
                  color: '#050A22',
                },
                '& svg': {
                  fontSize: '18px',
                },
              }}
              checked={!!value && value.unitsInfo.floorPlanIndex === floorPlan.floorPlanIndex}
            />
          </div>
          <div className="flex justify-between items-center">
            <Typography className="!font-Averta6 !text-[#050A22] !text-base">{`$${floorPlanPrice.minPrice.toLocaleString()} - $${floorPlanPrice.maxPrice.toLocaleString()}`}</Typography>
          </div>
        </div>
      </div>
      {selectFloorPlan && (
        <div className="mt-5">
          <Typography className="!font-averta !text-sm !text-[#050A22]">Select your preferred units (optional)</Typography>
          <div className="grid grid-cols-1 gap-y-3 mt-3">
            {Array.isArray(floorPlan.availableUnits) &&
              floorPlan.availableUnits.length > 0 &&
              floorPlan.availableUnits.map(unit => {
                const { id, name, rentPrices, availableStatus } = unit
                return (
                  <div key={id} aria-hidden="true" onClick={() => changeUnitItemSelect(unit)} className="rounded-lg bg-[#F7F9FC] p-2">
                    <div className="flex justify-between items-center leading-[30px]">
                      <Typography className="!font-Averta6 !text-[#050A22] !text-base !font-semibold">{name}</Typography>
                      <div className="flex items-center">
                        <Typography className="!font-Averta6 !text-[#050A22] !text-base !font-semibold">
                          {rentPrices.length > 0 ? `$${Number(rentPrices[0].discountAmount / 100).toLocaleString()}` : ''}
                        </Typography>
                        {rentPrices.length > 0 && rentPrices[0].leaseTerm && (
                          <Typography className="!ml-1 !text-[10px] lg:!text-xs !font-Averta4 !text-[#75798D]">
                            {rentPrices[0].leaseTerm} mo. lease
                          </Typography>
                        )}
                        &nbsp;&nbsp;
                        <Checkbox
                          sx={{
                            color: '#E0E2EF',
                            padding: '0',
                            '&.Mui-checked': {
                              color: '#050A22',
                            },
                            '& svg': {
                              fontSize: '18px',
                            },
                          }}
                          checked={!!value && value.unitsInfo.units.some(u => String(u.id) === String(id))}
                        />
                      </div>
                    </div>
                    {availableStatus === 0 && <div className="font-Averta4 text-[#050A22] text-xs">Tour available now</div>}
                    {availableStatus === 1 && (
                      <div className="font-Averta4 text-[#050A22] text-xs">
                        Tour available from {moment(unit.availableStartTime).format('MM/DD/YYYY')}
                      </div>
                    )}
                    {availableStatus === 2 && (
                      <div className="font-Averta4 text-[#050A22] text-xs">
                        Tour available from {moment(unit.preTourStartDate).format('MM/DD/YYYY')} to{' '}
                        {moment(unit.preTourEndDate).format('MM/DD/YYYY')}
                      </div>
                    )}
                  </div>
                )
              })}
            <div aria-hidden="true" onClick={changeOtherUnitSelect} className="rounded-lg bg-[#F7F9FC] p-2">
              <div className="flex justify-between items-center leading-[30px]">
                <div className="flex items-center justify-start">
                  <Typography className="!mr-2 !font-Averta6 !text-[#050A22] !text-base !font-semibold">Other Unit</Typography>
                  <input
                    value={value?.otherUnit.name}
                    onClick={e => e.stopPropagation()}
                    onChange={changeOtherUnitInput}
                    maxLength={10}
                    placeholder=" Unit Number"
                    className="border border-[#E0E2EF] font-Averta4 pl-[5px] rounded-[4px] w-[100px] leading-6 text-sm text-[#050A22]"
                  />
                </div>
                <Checkbox
                  sx={{
                    color: '#E0E2EF',
                    padding: '0',
                    '&.Mui-checked': {
                      color: '#050A22',
                    },
                    '& svg': {
                      fontSize: '18px',
                    },
                  }}
                  checked={!!value && value.otherUnit.floorPlanIndex === floorPlan.floorPlanIndex}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ScheduleTourFloorPlan
